// Google Analytics 4 + Consent Mode v2 (RGPD-friendly)
// - Le script GA4 n'est chargé que si REACT_APP_GA_MEASUREMENT_ID est défini
// - Le tracking est désactivé par défaut (denied) jusqu'au consentement explicite
// - Aucun cookie GA n'est posé tant que l'utilisateur n'a pas accepté

const STORAGE_KEY = "mc_cookie_consent_v1";  // 'granted' | 'denied'
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

let _initialized = false;

function _safeGet() {
  try { return localStorage.getItem(STORAGE_KEY); }
  catch (e) { console.warn("consent: read failed", e); return null; }
}

function _safeSet(value) {
  try { localStorage.setItem(STORAGE_KEY, value); }
  catch (e) { console.warn("consent: write failed", e); }
}

export function getConsent() {
  const v = _safeGet();
  return v === "granted" || v === "denied" ? v : null;
}

export function isAnalyticsEnabled() {
  return Boolean(GA_ID) && getConsent() === "granted";
}

function _ensureGtag() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  return window.gtag;
}

function _injectScript() {
  if (!GA_ID || _initialized || typeof document === "undefined") return;
  _initialized = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

/**
 * Initialise GA4 avec le Consent Mode v2.
 * Appelle ça une seule fois au boot de l'app.
 * Le script GA n'est injecté QUE si le visiteur a déjà accepté (CNIL-strict).
 */
export function initAnalytics() {
  if (!GA_ID) return;
  const gtag = _ensureGtag();
  if (!gtag) return;
  // Defaults: deny everything until consent
  const consent = getConsent();
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: consent === "granted" ? "granted" : "denied",
    wait_for_update: 500,
  });
  if (consent === "granted") {
    _injectScript();
    gtag("js", new Date());
    gtag("config", GA_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: false,
    });
  }
}

export function grantConsent() {
  _safeSet("granted");
  const gtag = _ensureGtag();
  if (!gtag || !GA_ID) return;
  gtag("consent", "update", { analytics_storage: "granted" });
  if (!_initialized) {
    _injectScript();
    gtag("js", new Date());
    gtag("config", GA_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: false,
    });
    // Send a page_view for the current page since the script was just loaded
    if (typeof window !== "undefined") {
      gtag("event", "page_view", {
        page_path: window.location.pathname + window.location.search,
        page_title: typeof document !== "undefined" ? document.title : "",
      });
    }
  }
}

export function denyConsent() {
  _safeSet("denied");
  const gtag = _ensureGtag();
  if (gtag && GA_ID) {
    gtag("consent", "update", { analytics_storage: "denied" });
  }
}

export function trackPageView(path, title) {
  if (!isAnalyticsEnabled()) return;
  const gtag = _ensureGtag();
  if (!gtag) return;
  gtag("event", "page_view", {
    page_path: path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
  });
}

export function trackEvent(name, params = {}) {
  if (!isAnalyticsEnabled()) return;
  const gtag = _ensureGtag();
  if (!gtag) return;
  gtag("event", name, params);
}
